import { useState, useCallback } from 'react';

interface CustomSelectProps {
  selected: string[];
  setSelected: (selected: string[]) => void;
  options: {
    name: string;
    id: string;
    location: string;
  }[];
  isDropdownOpen: boolean;
  setIsDropdownOpen: (isDropdownOpen: boolean) => void;
}

const CustomSelect = ({
  selected,
  setSelected,
  options,
  isDropdownOpen,
  setIsDropdownOpen,
}: CustomSelectProps) => {
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(!isDropdownOpen);
  }, [setIsDropdownOpen, isDropdownOpen]);

  const selectAllHandler = () => {
    if (selected?.length === options?.length) {
      setIsAllSelected(false);
      setSelected([]);
    } else {
      setIsAllSelected(true);
      setSelected(options.map((option) => option.id));
    }
  };

  const updateSelectedListHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { id, checked } = e.target;
    if (checked) {
      setSelected([...selected, id]);
      setIsAllSelected(selected.length + 1 === options.length);
    } else {
      setSelected(selected.filter((item) => item !== id));
      setIsAllSelected(false);
    }
  };

  return (
    <div className="w-full">
      <div
        onClick={toggleDropdown}
        data-testid="report-lounge-dropdown"
        className={
          isDropdownOpen
            ? 'flex h-[52px] w-full items-center justify-between rounded-t-lg border border-[#D1D5DB] bg-white p-4 text-sm xl:text-base'
            : 'flex h-[52px] w-full items-center justify-between rounded-lg border border-[#D1D5DB] bg-white p-4 text-sm xl:text-base'
        }
      >
        {selected && selected?.length < 1 && options?.length > 0 && (
          <span className="text-[#6B7280]">Select lounge...</span>
        )}
        {selected && selected.length > 0 && (
          <span className="w-[600px] truncate text-[#111928]">
            {selected
              ?.map(
                (item) => options.find((option) => option.id === item)?.name,
              )
              .join(', ')}
          </span>
        )}
        <svg
          className="ml-auto"
          width="12"
          height="13"
          viewBox="0 0 12 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.01475 9.73999C5.69651 9.73992 5.39134 9.61722 5.16635 9.39886L0.366346 4.74173C0.251734 4.63433 0.160315 4.50585 0.0974242 4.36381C0.0345334 4.22176 0.00142989 4.06898 4.53085e-05 3.91439C-0.00133927 3.7598 0.0290225 3.60649 0.0893596 3.4634C0.149697 3.32031 0.2388 3.19032 0.351472 3.081C0.464143 2.97168 0.598125 2.88523 0.745601 2.82669C0.893077 2.76815 1.05109 2.73869 1.21043 2.74003C1.36976 2.74138 1.52723 2.7735 1.67363 2.83451C1.82004 2.89553 1.95245 2.98423 2.06315 3.09543L6.01475 6.92941L9.96635 3.09543C10.1927 2.88335 10.4958 2.766 10.8104 2.76865C11.1251 2.7713 11.426 2.89375 11.6485 3.10962C11.871 3.32548 11.9972 3.6175 12 3.92277C12.0027 4.22804 11.8817 4.52214 11.6631 4.74173L6.86314 9.39886C6.63815 9.61722 6.33298 9.73992 6.01475 9.73999Z"
            fill="#9CA3AF"
          />
        </svg>
      </div>
      {isDropdownOpen && (
        <div className="h-[200px] w-full overflow-y-auto rounded-b-lg border-x border-b border-[#D1D5DB] bg-white p-4 2xl:h-[300px]">
          <div className="flex h-full w-full flex-col gap-5">
            <button
              onClick={selectAllHandler}
              data-testid="report-select-all-lounges"
              className="flex w-full cursor-pointer items-center gap-3 text-left text-sm font-medium text-[#1C64F2] xl:text-base"
            >
              <input
                checked={isAllSelected}
                type="checkbox"
                className="h-4 w-4 rounded-lg"
              />
              Select all
            </button>
            <div className="flex flex-col gap-5">
              {options.map((option) => (
                <label
                  htmlFor={option.id}
                  key={option.id}
                  className="flex h-[38px] w-full items-start gap-3 text-sm xl:text-base"
                >
                  <span className="pt-1">
                    <input
                      onChange={updateSelectedListHandler}
                      type="checkbox"
                      id={option.id}
                      className="h-4 w-4 rounded-lg"
                      checked={selected?.includes(option.id)} // Derive checked status from `selected`
                    />
                  </span>
                  <div className="flex flex-col">
                    <span className="text-[#111928]">{option.name}</span>
                    <span className="text-xs text-[#6B7280]">
                      {option.location}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default CustomSelect;
